<template>
  <v-dialog
    content-class="add-tags-popup"
    :value="internalValue"
    :width="630"
    @click:outside="onCloseClick"
    @keydown.esc="onCloseClick"
  >
    <v-card class="popup-wrapper">
      <div class='add-tags-popup'>
        <div class='add-tags-popup__title'>
          {{ $t('add.tags') }}
          <div class="close"
               @click="onCloseClick">
            X {{$t('newProject.esc')}}
          </div>
        </div>
        <div class='add-tags-popup__content'>
          <div class='add-tags-popup__content__input-tag'>
            <v-autocomplete
              :items="tagsToSuggest"
              :loading="tagsSuggestionsLoading"
              v-model="tags"
              @input.native="handleTagSuggestionInput"
              :rules="[rules.required]"
              @input="tagInputValue=''"
              :search-input.sync="tagInputValue"
              dense
              outlined
              autofocus
              return-object
              multiple
              allow-overflow
            />
          </div>
          <div class='add-tags-popup__content__selected-tags-list'>
            <v-chip
              label
              close
              pill
              small
              class='tag'
              v-for='(tag, index) in tags'
              :key='index'
              close-icon='close'
              @click:close='removeTagFromList(index)'
            >
              {{ tag }}
            </v-chip>
          </div>
          <div class='add-tags-popup__content__buttons'>
            <v-btn small depressed class="cancel-button" @click="onCloseClick"> {{ $t('confirm.cancel') }} </v-btn>
            <v-btn class="save-button" @click="onSaveTagsClick">
              {{ $t('add.tags.save') }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>

import { rules } from '@/utils/validationUtils';
import { CONFIGURATION } from '@/constants/configuration';
import { mapGetters } from 'vuex';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'AddTagsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  data() {
    return {
      tagsSuggestionsLoading: false,
      tags: [],
      tagInputValue: '',
    };
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    assets: {
      default: () => [],
      required: true,
    },
    isForOrganization: {
      type: Boolean,
      required: false,
      default: false,
    },
    isForCloudAccount: {
      type: Boolean,
      required: false,
      default: false,
    },
    isForInfrastructure: {
      type: Boolean,
      required: false,
      default: false,
    },
    organization: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  methods: {
    handleTagSuggestionInput(event) {
      this.tagInputValue = event.target.value;
    },
    removeTagFromList(index) {
      this.tags.splice(index, 1);
    },
    async onSaveTagsClick() {
      let items = [];
      if (this.isForOrganization) {
        const organizationTags = this.organizationsTags?.[this.organization.name] || {};
        items = [{
          url: this.organization.name,
          tags: { ...organizationTags, ...this.tagItems },
        }];
      } else if (this.isForCloudAccount) {
        items = [];
        this.assets.forEach(asset => {
          const cloudAccountTags = this.cloudAccountsTags?.[asset.configData] || {};
          items.push(
            {
              ...asset,
              tld: asset.configData,
              url: asset.configData,
              tags: { ...cloudAccountTags, ...this.tagItems },
            },
          );
        });
      } else if (this.isForInfrastructure) {
        items = {
          payload: this.assets,
          tags: {...this.tagItems},
        };
      } else {
        items = {
          payload: this.assets,
          tags: {...this.tagItems},
        };
      }
      await this.$emit('submitTagsInformation', { items, configType: this.configType });
      this.onCloseClick();
    },
    onCloseClick() {
      this.$emit('toggleTagsPopup');
      this.tags = [];
    },
  },
  computed: {
    ...mapGetters([
      'organizationsTags',
      'cloudAccountsTags',
      'infrastructureTagsSuggestions',
      'topLevelDomainTagsSuggestions',
    ]),
    tagsSuggestions() {
      let tagsArrays = [];
      if (this.isForOrganization) {
        tagsArrays = [...Object.values(this.organizationsTags)];
      } else if (this.isForCloudAccount) {
        tagsArrays = [...Object.values(this.cloudAccountsTags)];
      } else if (this.isForInfrastructure) {
        return [...this.infrastructureTagsSuggestions].sort();
      } else {
        return [...this.topLevelDomainTagsSuggestions].sort();
      }
      tagsArrays.filter(item => (Array.isArray(item) && !item.length ? {} : Object.keys(item).length));
      let suggestions = [];
      tagsArrays.forEach(currentTagsArray => {
        suggestions = suggestions.concat(Object.keys(currentTagsArray));
      });
      return Array.from(new Set(suggestions.concat(this.tags)));
    },
    configType() {
      if (this.isForOrganization) {
        return CONFIGURATION.CONFIG_TYPES.TAGS_ORGANIZATION;
      }
      if (this.isForCloudAccount) {
        return CONFIGURATION.CONFIG_TYPES.TAGS_CLOUD_ACCOUNT;
      }
      if (this.isForInfrastructure) {
        return CONFIGURATION.CONFIG_TYPES.TAGS_SUBDOMAIN;
      }
      return CONFIGURATION.CONFIG_TYPES.TAGS_DOMAIN;
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    rules() {
      return rules;
    },
    tagItems() {
      const items = {};
      this.tags.forEach(tag => {
        items[tag] = 'added';
      });
      return items;
    },
    tagsToSuggest() {
      const suggestions = (this.tagInputValue
        ? [this.tagInputValue]
        : []
      ).concat([...this.tagsSuggestions]);
      return suggestions;
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

// All styles must be unscoped because v-dialog renders outside component scope
.v-dialog.add-tags-popup {
  border-radius: rem(8) !important;
  overflow: visible;

  .popup-wrapper.v-card.v-card {
    background-color: var(--bg-primary) !important;
    border-radius: rem(8) !important;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15) !important;
    padding: rem(32) rem(40) !important;
  }

  .add-tags-popup {
    // Chip overrides for selected tags — match inventory row tag style
    .tag.v-chip.v-chip {
      background-color: var(--bg-secondary) !important;
      border: 1px solid var(--border-primary) !important;
      border-radius: rem(4) !important;
      padding: $space-1;
      font-size: $font-size-sm;
      font-family: $font-family-medium;
      color: #3572b0 !important;
    }
    .theme--dark & .tag.v-chip.v-chip {
      background-color: var(--bg-tertiary) !important;
      color: var(--text-primary) !important;
    }
    .tag.v-chip .v-icon {
      font-size: $font-size-base !important;
      color: var(--text-primary) !important;
    }

    // Title row
    &__title {
      text-transform: uppercase;
      color: var(--text-primary);
      font-size: rem(14);
      font-weight: 600;
      letter-spacing: 1.12px;
      line-height: rem(16);
      margin-bottom: rem(24);
      display: flex;
      justify-content: space-between;
      align-items: center;

      .close {
        cursor: pointer;
        font-size: $font-size-sm;
        text-transform: uppercase;
        text-decoration: none;
        color: var(--brand-primary);
        font-family: $default-application-font-medium;
        font-weight: 500;
        letter-spacing: 1.1px;
        line-height: rem(16);

        &:hover {
          opacity: 0.8;
        }
      }
    }

    // Content area
    &__content {
      &__input-tag {
        .v-text-field--outlined fieldset {
          border-color: var(--border-primary) !important;
        }
        .v-text-field--outlined.v-input--is-focused fieldset {
          border-color: var(--brand-primary) !important;
        }
        .v-input__slot {
          background-color: transparent !important;
        }
      }

      &__selected-tags-list {
        margin-top: rem(8);

        .tag {
          margin-right: rem(8);
        }
      }

      &__buttons {
        padding-top: rem(24);
        display: flex;
        justify-content: flex-end;
        gap: rem(8);

        .cancel-button.v-btn.v-btn {
          box-sizing: border-box;
          height: rem(32);
          min-width: rem(86);
          border: 1px solid var(--brand-primary) !important;
          color: var(--brand-primary) !important;
          border-radius: rem(4);
          background-color: transparent !important;
          box-shadow: none !important;
          font-size: $font-size-sm;
          font-weight: 500;
          letter-spacing: 1.07px;
        }

        .save-button.v-btn.v-btn {
          height: rem(32);
          min-width: rem(86);
          border-radius: rem(4);
          background-color: var(--brand-primary) !important;
          box-shadow: none !important;
          color: #ffffff !important;
          font-size: $font-size-sm;
          font-weight: 500;
          letter-spacing: 1.07px;
          line-height: rem(16);
          text-align: center;
        }
      }
    }
  }
}
</style>
